import React from "react";
import { Container } from "react-bootstrap";
import certData from "../../data/certifications.json";
import {
  Section,
  SectionLabel,
  SectionTitle,
  SectionDesc,
  MentorshipCallout,
  MentorshipLabel,
  MentorshipText,
  CertCard,
  CertRow,
  CertActions,
  CertName,
  CertIssuer,
  CertStatus,
  ProofBadge,
  ProofToggle,
  ProofPanel,
  ProofRow,
  ProofTabs,
  ProofTab,
  ProofInput,
  ProofSaveBtn,
  ProofExisting,
  SignInProofCTA,
} from "./CareerPath.styles";

const CERT_STATUS_LABEL = {
  to_be_started: "TO BE STARTED",
  in_progress: "IN PROGRESS",
  achieved: "ACHIEVED",
};

const certifications = certData.certifications;

const CertificationsGrid = ({
  user,
  signIn,
  proofs,
  certStatus,
  openCertProof,
  certProofTab,
  setCertProofTab,
  certProofUrl,
  setCertProofUrl,
  certUploading,
  onCertStatusToggle,
  onCertProofToggle,
  onCertSaveLink,
  onCertUploadFile,
  onCertRemoveProof,
}) => (
  <Section>
    <Container>
      <SectionLabel>CERTIFICATIONS</SectionLabel>
      <SectionTitle>Proof of work</SectionTitle>
      <SectionDesc>
        Certifications don't make you good at your job. But they prove you sat down
        and studied the fundamentals. Here's what we track.
      </SectionDesc>

      <MentorshipCallout>
        <MentorshipLabel>MENTORSHIP GROUPS</MentorshipLabel>
        <MentorshipText>
          Not sure which cert to chase first? We run small mentorship groups that work
          through these certifications together — weekly study sessions, exam prep,
          and a senior to debug your blockers.
        </MentorshipText>
      </MentorshipCallout>

      {certifications.map((cert, i) => {
        const status = certStatus(cert);
        const proof = proofs[cert.name];
        const hasAttachment = proof && (proof.type === "link" || proof.type === "file");
        const open = openCertProof === cert.name;
        return (
          <CertCard key={i}>
            <CertRow>
              <div>
                <CertName>
                  {cert.name}
                  {hasAttachment && <ProofBadge>· proof attached</ProofBadge>}
                </CertName>
                <CertIssuer>{cert.issuer}</CertIssuer>
              </div>
              <CertActions>
                <CertStatus
                  type="button"
                  status={status}
                  onClick={() => onCertStatusToggle(cert)}
                  disabled={!user}
                  title={user ? "Click to cycle status" : "Sign in to change status"}
                >
                  {CERT_STATUS_LABEL[status]}
                </CertStatus>
                <ProofToggle
                  type="button"
                  onClick={() => onCertProofToggle(cert.name)}
                >
                  {hasAttachment ? "Edit proof" : "Add proof"}
                </ProofToggle>
              </CertActions>
            </CertRow>

            <ProofPanel open={open}>
              {!user ? (
                <ProofRow>
                  <span style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 11,
                    letterSpacing: "0.06em",
                    color: "var(--nd-text-secondary)",
                  }}>
                    Sign in to attach proof
                  </span>
                  <SignInProofCTA type="button" onClick={signIn}>
                    Sign in with Google
                  </SignInProofCTA>
                </ProofRow>
              ) : (
                <>
                  <ProofTabs>
                    <ProofTab type="button" active={certProofTab === "link"} onClick={() => setCertProofTab("link")}>Link</ProofTab>
                    <ProofTab type="button" active={certProofTab === "file"} onClick={() => setCertProofTab("file")}>File</ProofTab>
                  </ProofTabs>
                  {certProofTab === "link" ? (
                    <ProofRow>
                      <ProofInput
                        type="url"
                        placeholder="https://… certification or repo URL"
                        value={certProofUrl}
                        onChange={(e) => setCertProofUrl(e.target.value)}
                      />
                      <ProofSaveBtn
                        type="button"
                        disabled={!certProofUrl}
                        onClick={() => onCertSaveLink(cert.name)}
                      >
                        Save
                      </ProofSaveBtn>
                    </ProofRow>
                  ) : (
                    <ProofRow>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        style={{
                          fontFamily: "'Space Mono', monospace",
                          fontSize: 12,
                          color: "var(--nd-text-secondary)",
                        }}
                        onChange={(e) => onCertUploadFile(cert.name, e.target.files[0])}
                      />
                      {certUploading && (
                        <span style={{
                          fontFamily: "'Space Mono', monospace",
                          fontSize: 10,
                          letterSpacing: "0.08em",
                          color: "var(--nd-text-disabled)",
                        }}>
                          Uploading…
                        </span>
                      )}
                    </ProofRow>
                  )}
                  {hasAttachment && (
                    <ProofRow style={{ marginTop: 12 }}>
                      <ProofExisting href={proof.url} target="_blank" rel="noopener noreferrer">
                        ↗ {proof.name}
                      </ProofExisting>
                      <ProofSaveBtn
                        type="button"
                        onClick={() => onCertRemoveProof(cert.name)}
                        style={{ background: "transparent", color: "var(--nd-accent)", border: "1px solid var(--nd-accent)" }}
                      >
                        Remove
                      </ProofSaveBtn>
                    </ProofRow>
                  )}
                </>
              )}
            </ProofPanel>
          </CertCard>
        );
      })}
    </Container>
  </Section>
);

export default CertificationsGrid;
